/**
 * The navigation rail, ported from the legacy markup class-for-class.
 *
 * The grouping and the one-line descriptions under each heading are deliberate — they tell
 * a manager what each section is for, and they were written by the people who use this.
 * Kept verbatim; only the click handling becomes routing.
 */
import { NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Icon } from './IconSprite.jsx';
import { useModel, useDepartmentSignals } from '../state.jsx';

const navClass = ({ isActive }) => 'sb-nav-item' + (isActive ? ' active' : '');

/**
 * `collapsible` groups can be folded away. They start open: the legacy build shipped Deeper
 * Insights collapsed, which hid four working screens behind a chevron nobody had reason to
 * click.
 */
function Group({ label, hint, children, collapsible = false }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className={'sb-group' + (collapsed ? ' collapsed' : '')}>
      <div
        className={'sb-group-hdr' + (collapsible ? ' collapsible' : '')}
        onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
      >
        <span className="sb-section-label">{label}</span>
        {collapsible && <span className="sb-group-chevron">▾</span>}
      </div>
      {hint && (
        <div style={{ fontSize: 10, color: 'var(--ink4)', padding: '0 20px 8px', marginTop: -4 }}>
          {hint}
        </div>
      )}
      <div className="sb-group-body">{children}</div>
    </div>
  );
}

function Item({ to, icon, children, badge, badgeStyle }) {
  return (
    <NavLink to={to} className={navClass} end>
      <span className="sb-icon"><Icon name={icon} /></span> {children}
      {badge != null && <span className="sb-badge-s" style={badgeStyle}>{badge}</span>}
    </NavLink>
  );
}

/** Status colour for a department dot — the same mapping the tiles use. */
function dotColor(signal) {
  if (signal.dataInvalid) return 'var(--purple)';
  if (signal.notConfigured) return 'var(--grey)';
  if (signal.flagged) return 'var(--red)';
  if (signal.watch) return 'var(--amber)';
  if (signal.kind === 'judgment') return 'var(--blue)';
  return 'var(--grn)';
}

export function Sidebar() {
  const { state, activeDept, setActiveDept } = useModel();
  const signals = useDepartmentSignals();
  const navigate = useNavigate();

  const branding = state?.branding ?? {};
  const counts = {
    team: state?.team?.length ?? 0,
    outages: state?.outages?.length ?? 0,
    pipeline: state?.pipeline?.length ?? 0,
    impls: state?.impls?.length ?? 0,
  };

  return (
    <nav id="sidebar">
      <div className="sb-brand">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div
            id="sb-logo-wrap"
            style={{
              width: 38, height: 38, borderRadius: 10, overflow: 'hidden', flexShrink: 0,
              boxShadow: '0 2px 8px rgba(11,195,169,.4)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg,var(--teal),var(--teal2))',
            }}
          >
            {branding.logo
              ? <img src={branding.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : <span style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>⚡</span>}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="sb-name">{branding.org ?? 'The Energy Authority'}</div>
            <div className="sb-sub">Workforce Capacity</div>
          </div>
        </div>
        <div style={{ padding: '8px 0 0', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--teal)', lineHeight: 1 }}>
            Capaci<span style={{ color: 'var(--ink)' }}>TEA</span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--ink4)', letterSpacing: 1, textTransform: 'uppercase', marginTop: 2 }}>
            v20 · People Operations
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        <Group label="Home" hint="One page — what needs a decision, and what doesn't.">
          <NavLink to="/executive" className={navClass} style={{ background: 'var(--teal3)', margin: '1px 8px 6px' }}>
            <span className="sb-icon"><Icon name="ic-target" /></span> Executive Summary
          </NavLink>
        </Group>

        <Group label="My Departments" hint="Check on health and how each one is measured.">
          <Item to="/" icon="ic-bars">Department Status</Item>
          <Item to="/coverage" icon="ic-shield">Coverage Risk</Item>
          <Item to="/configure" icon="ic-sliders">Capacity Method &amp; Inputs</Item>
        </Group>

        <Group label="Team &amp; Workload" hint="The people and work behind each department's numbers.">
          <Item to="/manage/team" icon="ic-users" badge={counts.team}>Team Roster</Item>
          <Item to="/manage/outages" icon="ic-cal-x" badge={counts.outages}>Known Outages</Item>
          <Item to="/manage/pipeline" icon="ic-trend" badge={counts.pipeline}>Anticipated Demand</Item>
          <Item to="/manage/projects" icon="ic-wrench" badge={counts.impls}>Active Projects</Item>
        </Group>

        <Group label="Hiring Decisions" hint="Turns the live signal into a recommendation.">
          <Item to="/analysis/decision" icon="ic-scale">Hire / No-Hire</Item>
          <Item to="/analysis/bizcase" icon="ic-coin">Business Case</Item>
          <Item to="/configure/hcplan" icon="ic-layers">Built Positions</Item>
        </Group>

        <Group label="Reports">
          <Item to="/reports" icon="ic-file">Reports</Item>
        </Group>

        <Group label="Deeper Insights" collapsible>
          <div style={{ fontSize: 10, color: 'var(--ink4)', padding: '0 20px 8px' }}>
            Supplementary context — not a second source of truth.
          </div>
          <Item to="/manage/output" icon="ic-gauge" badge="Pilot" badgeStyle={{ color: 'var(--red)' }}>
            Team Output
          </Item>
          <Item to="/analysis/fragility" icon="ic-pulse">Team Fragility</Item>
          <Item to="/manage/succession" icon="ic-branch">Cascading Roles</Item>
          <Item to="/analysis/compare" icon="ic-shuffle">Scenario Compare</Item>
        </Group>

        <div className="sb-section-label">Departments</div>
        <div className="sb-dept-scroll">
          <div
            className={'sb-dept-item' + (activeDept === 'ALL' ? ' active' : '')}
            onClick={() => setActiveDept('ALL')}
          >
            <span className="sb-dept-dot" style={{ background: 'var(--teal)' }} />
            All Departments
          </div>
          {signals
            .filter(({ dep }) => !dep.parent)
            .map(({ dep, signal }) => (
              <div key={dep.id}>
                <div
                  className={'sb-dept-item' + (activeDept === dep.id ? ' active' : '')}
                  onClick={() => setActiveDept(dep.id)}
                >
                  <span className="sb-dept-dot" style={{ background: dotColor(signal) }} />
                  {dep.name}
                </div>
                <div className="sb-dept-children">
                  {signals.filter((c) => c.dep.parent === dep.id).map((c) => (
                    <div
                      key={c.dep.id}
                      className={'sb-dept-item' + (activeDept === c.dep.id ? ' active' : '')}
                      onClick={() => setActiveDept(c.dep.id)}
                    >
                      <span className="sb-dept-dot" style={{ background: dotColor(c.signal) }} />
                      {c.dep.name}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="sb-footer">
        <button className="sb-footer-btn cfg" onClick={() => navigate('/configure/howto')}>? Help</button>
      </div>
    </nav>
  );
}
